import { Alert, AlertDescription, AlertTitle } from '@ophelos/ui/components/alert';
import { Button } from '@ophelos/ui/components/button';
import { Loader } from '@ophelos/ui/components/loader';
import { CircleAlert } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { SettingsMenu } from '@/components/ui/SettingsMenu';
import { useAuth } from '@/contexts/customer-auth-context';
import { useConfiguration } from '@/contexts/ConfigurationContext';
import { useDebtData } from '@/contexts/DebtDataContext';
import { useTranslation } from '@/hooks/useTranslation';
import { ConnectApiError, getAuthSessionFromConnect, logPortalActivityToConnect } from '@/lib/connect-api';
import { persistLimitedSession } from '@/lib/customer-session';
import { bindGlobalCellFromCode } from '@/lib/global/resolve-code';
import { isPayableDebt } from '@/lib/debtFilters';
import { isGlobalSurface } from '@/lib/surface-mode';

type Phase = 'establishing' | 'preparing' | 'invalid' | 'nothingDue' | 'problem' | 'wrongAccount';

// A link names a case by the tail of its id, which is where uuidv7 puts its randomness.
const CASE_SELECTOR_LENGTH = 5;

// What this page will act on from a link. Deliberately not shared with the sender's list in
// forest/templates/portal_links.py: that one bounds what Forest may emit, this one bounds
// what an untrusted URL may carry, and they are free to differ. `offer` is
// absent on purpose — it is the budget assessment's own hand-back marker, and an operator
// link must not be able to force that confirmation pause.
const LANDING_PARAMS = ['tab', 'amount', 'frequency', 'start', 'count'] as const;
const LANDING_TABS = ['full', 'partial', 'plan'];

const selectorFor = (caseId: string) => caseId.slice(-CASE_SELECTOR_LENGTH).toUpperCase();

/**
 * The landing params a link carries, dropping anything a payment page would not read.
 *
 * An unusable tab is dropped rather than failing the link: the payment page falls back to
 * its own first available tab, which is where an unroutable landing belongs anyway.
 */
function landingFrom(search: string): URLSearchParams {
  const carried = new URLSearchParams(search);
  const kept = new URLSearchParams();
  for (const name of LANDING_PARAMS) {
    const value = carried.get(name);
    if (!value) continue;
    if (name === 'tab' && !LANDING_TABS.includes(value)) continue;
    kept.set(name, value);
  }
  return kept;
}

/**
 * Signs the customer in from a link and sends them to the case it names.
 *
 * Arriving counts as a login: the same `login` activity the code form writes, so journeys,
 * ODE and analytics treat a link visit and a typed code alike. A mail gateway that runs
 * page scripts would record one too; that is accepted over losing the signal.
 */
export default function ClickToPay() {
  const { code, caseRef } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Derived from the search string rather than read at each use: the redirects below pass it
  // on, and this route is the only way in from an email, so a landing lost here is a link
  // that silently does nothing.
  const landing = useMemo(() => landingFrom(location.search), [location.search]);
  const { t } = useTranslation();
  const { isAuthenticated, isLoading: authLoading, user, accountId: signedInAccountId } = useAuth();
  const { loadAccountConfigAndReturn } = useConfiguration();
  const { debts, account, isLoading: debtsLoading, error: debtsError, refreshDebts } = useDebtData();

  const [phase, setPhase] = useState<Phase>('establishing');
  const [accountId, setAccountId] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const hasExchanged = useRef(false);
  const hasRouted = useRef(false);
  const hasLoggedActivity = useRef(false);

  const accessCode = code?.trim().toUpperCase() ?? '';
  const selector = caseRef?.trim().toUpperCase() ?? '';

  const retry = () => {
    hasExchanged.current = false;
    hasRouted.current = false;
    setPhase('establishing');
    setAttempt((n) => n + 1);
    // Re-running the exchange alone won't clear the debt provider's sticky error.
    void refreshDebts();
  };

  useEffect(() => {
    if (hasExchanged.current) return;

    window.history.replaceState(null, '', '/p');

    // AuthKit may still be restoring a WorkOS session. Deciding against a half-restored
    // user would let a stale closure persist a limited session over the full one.
    if (authLoading) return;
    hasExchanged.current = true;

    // Only the top document may run the exchange — a hidden iframe could plant a session silently.
    if (window.self !== window.top || accessCode.length === 0) {
      setPhase('invalid');
      return;
    }

    async function establish() {
      let session: Awaited<ReturnType<typeof getAuthSessionFromConnect>>;
      try {
        if (isGlobalSurface()) await bindGlobalCellFromCode(accessCode);
        session = await getAuthSessionFromConnect(accessCode);
      } catch (error) {
        // Only a rejected code means the link is bad; a throttle or outage must not.
        const status = error instanceof ConnectApiError ? error.status : undefined;
        throw status === 401 || status === 404 ? new InvalidLink() : error;
      }

      // A live WorkOS session wins: overwriting it with a limited one leaves checkout
      // with no usable credential (the token lookup keys off the stored stage). Which
      // account it belongs to comes from the same storage, so when that is gone there
      // is no full credential left to protect and the link may establish its own.
      if (user && signedInAccountId) {
        if (signedInAccountId !== session.account_id) {
          setPhase('wrongAccount');
          return;
        }
      } else {
        await persistLimitedSession(accessCode, session, loadAccountConfigAndReturn);
        // Retry re-runs this whole exchange (a downstream debts-load failure, not a bad
        // credential), so the ref — unlike hasExchanged — survives it: one link visit
        // logs one activity no matter how many times establish() itself reruns.
        if (!hasLoggedActivity.current) {
          hasLoggedActivity.current = true;
          logPortalActivityToConnect(session.account_id, 'login', { source: 'click_to_pay' }, 'limited').catch(
            (error) => console.error('Failed to log portal activity:', error)
          );
        }
      }

      setAccountId(session.account_id);
      setPhase('preparing');
    }

    establish().catch((error) => {
      console.error('Click-to-pay could not establish a session:', error);
      setPhase(error instanceof InvalidLink ? 'invalid' : 'problem');
    });
  }, [accessCode, loadAccountConfigAndReturn, attempt, user, signedInAccountId, authLoading]);

  useEffect(() => {
    if (phase !== 'preparing' || hasRouted.current) return;
    if (debtsLoading || !accountId) return;

    // Any settled error, whichever account's load produced it: the provider neither
    // retries nor drops the account it already holds, so waiting one out is waiting
    // forever. The retry re-runs the load rather than reasoning about whose error it was.
    if (debtsError) {
      hasRouted.current = true;
      setPhase('problem');
      return;
    }
    if (account == null || account.id !== accountId) return;

    const payable = debts.filter(isPayableDebt);
    hasRouted.current = true;

    // A link asking for plan setup on a case that is already arranged still has somewhere to
    // go — the plan it names. `isPayableDebt` excludes an arranged case, so this has to be
    // decided before the nothing-due branch below swallows it.
    if (landing.get('tab') === 'plan' && selector) {
      const named = debts.filter((debt) => selectorFor(debt.id) === selector);
      if (named.length === 1 && named[0].paymentPlan) {
        navigate(`/payment-plan/${named[0].id}`, { replace: true });
        return;
      }
    }

    if (payable.length === 0) {
      setPhase('nothingDue');
      return;
    }

    // Carried to both surfaces: `Payment` and `AccountPayment` read `tab`, `frequency`,
    // `start` and `count` alike, and an account-wide offer's link has no selector, so the
    // account branch is where its landing arrives.
    const query = landing.toString();
    const withLanding = (path: string) => (query ? `${path}?${query}` : path);

    // Exactly one match required — a collision falls back to account routing, never picks one.
    const matches = selector ? payable.filter((debt) => selectorFor(debt.id) === selector) : [];
    if (matches.length === 1) {
      navigate(withLanding(`/payment/${matches[0].id}`), { replace: true });
      return;
    }

    const destination = payable.length === 1 ? `/payment/${payable[0].id}` : '/account-payment';
    navigate(withLanding(destination), { replace: true });
  }, [phase, debtsError, debtsLoading, accountId, account, debts, selector, navigate, landing]);

  if (phase === 'establishing' || phase === 'preparing') {
    return <Loader fullScreen />;
  }

  const NOTICES = {
    invalid: {
      variant: 'destructive',
      cta: isAuthenticated ? 'viewYourAccount' : 'enterCodeInstead',
      onCta: () => navigate(isAuthenticated ? '/dashboard' : '/login', { replace: true }),
    },
    nothingDue: { variant: 'info', cta: 'viewYourAccount', onCta: () => navigate('/dashboard', { replace: true }) },
    wrongAccount: { variant: 'info', cta: 'viewYourAccount', onCta: () => navigate('/dashboard', { replace: true }) },
    problem: { variant: 'warning', cta: 'tryAgain', onCta: retry },
  } as const;
  const notice = NOTICES[phase];

  return (
    <Layout>
      <SettingsMenu size="icon-sm" className="absolute top-4 max-md:left-4 md:right-4 z-20" />
      <div className="w-full max-w-lg px-4 stack gap-6 relative z-10 mt-[8vh] md:mt-[12vh] mx-auto">
        <Alert variant={notice.variant}>
          <CircleAlert />
          <AlertTitle>{t(`clickToPay.${phase}Title`)}</AlertTitle>
          <AlertDescription>{t(`clickToPay.${phase}Description`)}</AlertDescription>
        </Alert>
        <Button size="xl" className="w-full" onClick={notice.onCta}>
          {t(`clickToPay.${notice.cta}`)}
        </Button>
      </div>
    </Layout>
  );
}

class InvalidLink extends Error {}
